import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import AppContext from './context/Context';
import toggleRTL from './helpers/toggleRTL';
import {
  AUTH_STORAGE_KEYS,
  clearStoredAuthSession,
  getAuthHeaders,
  getAuthToken,
  getItemFromStore,
  getStoredAuthUser,
  setItemToStore
} from './helpers/utils';
import { API_BASE_CANDIDATES } from './apiConfig';

const browserBootstrap =
  typeof window !== 'undefined' && window.__PSI_PADMIN_BOOTSTRAP__
    ? window.__PSI_PADMIN_BOOTSTRAP__
    : null;
const hasWindow = typeof window !== 'undefined';
const storedAuthUser = hasWindow ? getStoredAuthUser() : null;
const storedAuthToken = hasWindow ? getAuthToken() : null;

const sessionEndpointCandidates = API_BASE_CANDIDATES.map(base => `${base}/p-admin/auth.php?action=me`);

const Main = ({ children }) => {
  const [isFluid, setIsFluid] = useState(getItemFromStore('isFluid', true));
  const [isRTL, setIsRTL] = useState(getItemFromStore('isRTL', false));
  const [isDark, setIsDark] = useState(getItemFromStore('isDark', false));
  const [navbarPosition, setNavbarPosition] = useState(getItemFromStore('navbarPosition', 'vertical'));
  const [showBurgerMenu, setShowBurgerMenu] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(getItemFromStore('isSidebarCollapsed', false));
  const [currency, setCurrency] = useState("so'm");
  const [user, setUser] = useState(browserBootstrap?.user || storedAuthUser || null);
  const [token, setToken] = useState(browserBootstrap?.token || storedAuthToken || null);
  const [taskNotify, setTaskNotify] = useState([]);
  const [sessionReady, setSessionReady] = useState(!!browserBootstrap?.user);
  const [dashboardMeta, setDashboardMeta] = useState(browserBootstrap || null);

  const clearAuth = () => {
    setUser(null);
    setToken(null);
    setDashboardMeta(null);
    setSessionReady(true);

    clearStoredAuthSession();
  };

  useEffect(() => {
    setItemToStore('isDark', isDark);
    if (isDark) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
  }, [isDark]);

  useEffect(() => {
    setItemToStore('isFluid', isFluid);
  }, [isFluid]);

  useEffect(() => {
    setItemToStore('isRTL', isRTL);
    toggleRTL(isRTL);
  }, [isRTL]);

  useEffect(() => {
    setItemToStore('navbarPosition', navbarPosition);
  }, [navbarPosition]);

  useEffect(() => {
    setItemToStore('isSidebarCollapsed', isSidebarCollapsed);
  }, [isSidebarCollapsed]);

  useEffect(() => {
    if (!user) {
      return;
    }

    setDashboardMeta(current => ({
      ...(current || {}),
      user
    }));
  }, [user]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      return undefined;
    }

    delete axios.defaults.headers.common.Authorization;
    return undefined;
  }, [token]);

  useEffect(() => {
    axios.defaults.withCredentials = true;
  }, []);

  useEffect(() => {
    if (!hasWindow) {
      return;
    }

    if (token) {
      setItemToStore(AUTH_STORAGE_KEYS.token, token, window.localStorage);
      return;
    }

    window.localStorage.removeItem(AUTH_STORAGE_KEYS.token);
  }, [token]);

  useEffect(() => {
    if (!hasWindow) {
      return;
    }

    if (user) {
      setItemToStore(AUTH_STORAGE_KEYS.user, user, window.localStorage);
      return;
    }

    window.localStorage.removeItem(AUTH_STORAGE_KEYS.user);
  }, [user]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          clearAuth();

          if (hasWindow && !window.location.pathname.includes('/authentication/')) {
            window.location.assign('/authentication/basic/login?panel=admin');
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (browserBootstrap?.user) {
      setSessionReady(true);
    }

    const requestSession = async () => {
      let lastError = null;

      for (const endpoint of sessionEndpointCandidates) {
        try {
          return await axios.get(endpoint, {
            timeout: 12000,
            headers: getAuthHeaders(),
            withCredentials: true
          });
        } catch (error) {
          lastError = error;

          const status = error.response?.status || 0;
          if (status && ![404, 502, 503, 504].includes(status)) {
            throw error;
          }
        }
      }

      throw lastError || new Error('Sessiya endpointi topilmadi');
    };

    const fetchSession = async () => {
      try {
        const response = await requestSession();

        if (cancelled) {
          return;
        }

        if (response.data && response.data.success) {
          setUser(response.data.user);
          setDashboardMeta({ user: response.data.user });
          setSessionReady(true);
          return;
        }
      } catch (error) {
        if (!cancelled && !browserBootstrap?.user) {
          clearAuth();
        }
      }

      if (!cancelled) {
        setSessionReady(true);
      }
    };

    fetchSession();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AppContext.Provider
      value={{
        isFluid,
        setIsFluid,
        isRTL,
        setIsRTL,
        isDark,
        setIsDark,
        navbarPosition,
        setNavbarPosition,
        showBurgerMenu,
        setShowBurgerMenu,
        isSidebarCollapsed,
        setIsSidebarCollapsed,
        currency,
        setCurrency,
        user,
        setUser,
        token,
        setToken,
        clearAuth,
        taskNotify,
        setTaskNotify,
        sessionReady,
        setSessionReady,
        dashboardMeta,
        setDashboardMeta
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

Main.propTypes = {
  children: PropTypes.node
};

export default Main;
